/**
 * Key the recent terms are stored under. Per browser, per origin: these never
 * leave the device and are never read back by the server.
 *
 * @const STORAGE_KEY
 * @hidden
 */
const STORAGE_KEY = 'search.recent';
/**
 * Terms kept. Enough to cover a working session, few enough that the list
 * stays scannable under the field.
 *
 * @const MAX_RECENT
 * @since 1.0.0
 */
export const MAX_RECENT = 6;
/**
 * Shortest term worth remembering, matching the one the field searches on.
 *
 * @hidden
 */
const MIN_LENGTH = 2;
/**
 * Reads the recent terms.
 *
 * Every access is guarded: localStorage throws in a private window and comes
 * back empty when site data is cleared, and a search field must keep working
 * in both cases.
 *
 * @function readRecentSearches
 * @since 1.0.0
 */
export function readRecentSearches() {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return [];
        }
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) {
            return [];
        }
        return parsed
            .filter((value) => typeof value === 'string')
            .map((value) => value.trim())
            .filter(Boolean)
            .slice(0, MAX_RECENT);
    }
    catch (e) {
        return [];
    }
}
/**
 * Remembers a term, most recent first, without duplicates.
 *
 * Returns the new list so the caller can render it without reading back.
 *
 * @function rememberSearch
 * @since 1.0.0
 */
export function rememberSearch(term) {
    const trimmed = String(term !== null && term !== void 0 ? term : '').trim();
    if (trimmed.length < MIN_LENGTH) {
        return readRecentSearches();
    }
    // Case-insensitive de-duplication, keeping the casing just typed.
    const previous = readRecentSearches().filter((value) => value.toLowerCase() !== trimmed.toLowerCase());
    const next = [trimmed, ...previous].slice(0, MAX_RECENT);
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    }
    catch (e) {
        // Nothing to do: the list is a convenience, not state the app relies on.
    }
    return next;
}
/**
 * @function clearRecentSearches
 * @since 1.0.0
 */
export function clearRecentSearches() {
    try {
        window.localStorage.removeItem(STORAGE_KEY);
    }
    catch (e) {
        // Same as above.
    }
    return [];
}
